import {
  type Ref,
  type RefAttributes,
  forwardRef,
  useMemo,
  useState,
} from 'react';

import {
  Autocomplete,
  type AutocompleteOwnerState,
  type AutocompleteProps,
  type AutocompleteRenderOptionState,
  CircularProgress,
  type InputProps,
  TextField,
  debounce,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';

import type { RaAutoCompleteCreatableItem } from '@modules/_shared/components/data-grid/RaAutoComplete';
import { useLocale } from '@shared/hooks/locale';

export type AutocompleteMultiProps<T> = {
  values: T[];
  onFilter: (inputStr: string) => Promise<T[]> | T[];
  loading?: boolean;
  error?: boolean;
  disabled?: boolean;
  InputProps?: InputProps;
  noDebounce?: boolean;
  /**
   * v9 rename of pre-v9 `renderTags`. Same shape as MUI v9's `Autocomplete`
   * `renderValue`: `(value, getItemProps, ownerState) => ReactNode`. React 19
   * does not accept `key` via spread, so consumers must pull `key` out of
   * `getItemProps({ index })` and put it directly on the JSX element.
   */
  renderTags?: NonNullable<
    AutocompleteProps<T, true, false, false>['renderValue']
  >;
} & Pick<
  AutocompleteProps<T, true, false, false>,
  | 'getOptionLabel'
  | 'isOptionEqualToValue'
  | 'groupBy'
  | 'slotProps'
  | 'fullWidth'
> &
  (
    | ({ onChange: (values: T[]) => void; creatable?: false } & Pick<
        AutocompleteProps<T, true, false, false>,
        'renderOption'
      >)
    | {
        creatable:
          | true
          | {
              createItemFn?: (inputValue: string) => any;
              elementPosition: 'first' | 'last';
            };
        onChange: (values: (T | RaAutoCompleteCreatableItem<T>)[]) => void;
        renderOption?: (
          props: React.HTMLAttributes<HTMLLIElement>,
          option: T | RaAutoCompleteCreatableItem<T>,
          state: AutocompleteRenderOptionState,
          ownerState: AutocompleteOwnerState<T, true, false, false>,
        ) => React.ReactNode;
      }
  );

const AutocompleteMultiWithRef = <T,>(
  props: AutocompleteMultiProps<T>,
  ref: Ref<HTMLDivElement>,
): React.ReactNode => {
  const {
    values,
    onFilter,
    loading,
    error,
    disabled,
    InputProps,
    noDebounce,
    onChange,
    renderOption,
    getOptionLabel,
    isOptionEqualToValue,
    renderTags,
    groupBy,
    slotProps,
    fullWidth = true,
  } = props;

  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<T[]>([]);

  const createOptions = useMemo(
    () =>
      props.creatable != null && props.creatable !== false
        ? {
            createItemFn:
              typeof props.creatable !== 'boolean' &&
              props.creatable.createItemFn != null
                ? props.creatable.createItemFn
                : null,
            elementPosition:
              typeof props.creatable !== 'boolean'
                ? props.creatable.elementPosition
                : 'last',
          }
        : null,
    [props],
  );

  const maybeDebouncedSearch = useMemo(() => {
    const searchFn = async (value: string) => {
      // Convert result to Promise if the onFilter function is synchronous.
      const result = await Promise.resolve(onFilter(value));

      setOptions(result);
    };

    return noDebounce === true ? searchFn : debounce(searchFn, 275);
  }, [noDebounce, onFilter]);

  const handleSearch = async (inputStr?: string) => {
    setOpen(true);
    maybeDebouncedSearch(inputStr ?? '');
  };

  return (
    <Autocomplete<T, true>
      multiple
      ref={ref}
      autoComplete={false}
      autoCorrect="off"
      open={open}
      fullWidth={fullWidth}
      filterSelectedOptions
      disabled={disabled}
      onOpen={() => {
        handleSearch('');
      }}
      onClose={() => {
        setOpen(false);
        setOptions([]);
      }}
      onFocus={() => {
        handleSearch('');
      }}
      value={values}
      getOptionLabel={getOptionLabel}
      options={options}
      loading={loading}
      loadingText={t('Loading...')}
      noOptionsText={t('No results')}
      isOptionEqualToValue={isOptionEqualToValue}
      disableCloseOnSelect
      popupIcon={<ChevronDown size={16} />}
      onInputChange={(_, value) => handleSearch(value)}
      onChange={(_, values) => onChange(values)}
      filterOptions={(filteredResults, state) => {
        if (createOptions == null) {
          return filteredResults;
        }

        const createItem =
          createOptions.createItemFn?.(state.inputValue) ??
          ({
            inputValue: state.inputValue,
            new: true,
          } as RaAutoCompleteCreatableItem<T>);

        return createOptions.elementPosition === 'first'
          ? [createItem, ...filteredResults]
          : [...filteredResults, createItem];
      }}
      renderOption={renderOption}
      renderValue={renderTags}
      groupBy={groupBy}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          sx={{ background: 'var(--mui-palette-background-paper)' }}
          error={error}
          size="small"
          slotProps={{
            ...params.slotProps,

            input: {
              ...InputProps,
              ...params.slotProps.input,
              startAdornment: (
                <>
                  {InputProps?.startAdornment}
                  {params.slotProps.input.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} disableShrink />
                  ) : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },

            htmlInput: {
              ...params.slotProps.htmlInput,
              autoComplete: 'one-time-code', // disable autocomplete and autofill
              type: 'new-password',
              name: 'new-password',
              autoCorrect: 'off',
              spellCheck: 'false',
            },
          }}
        />
      )}
      slotProps={{
        ...(slotProps ?? {}),
        paper: {
          elevation: 0,
          ...(slotProps?.paper ?? {}),
        },
      }}
    />
  );
};

export const AutocompleteMulti = forwardRef(AutocompleteMultiWithRef) as <T>(
  props: AutocompleteMultiProps<T> & RefAttributes<HTMLDivElement>,
) => React.ReactNode;
